import { api } from './api-client';

type SuccessEnvelope<T> = { data: T };

export const GOOGLE_SEARCH_CONSOLE = 'google_search_console';

export type SearchConsoleConnection = {
  id: string;
  organizationId: string;
  projectId: string | null;
  siteUrl: string | null;
  status: string;
  lastError: string | null;
  connectedAt: string | null;
};

/** A verified property on the connecting user's Google account. */
export type SearchConsoleSite = {
  siteUrl: string;
  permissionLevel: string;
};

// No token here by design: the refresh token is vaulted server-side during the
// callback, and the client only ever holds the connection row id.
export type SearchConsoleCallbackResult = {
  projectId: string;
  connectionId: string;
  sites: SearchConsoleSite[];
};

const BASE = '/integrations/google-search-console';

export const SearchConsoleService = {
  getConnection: async (
    projectId: string,
  ): Promise<SearchConsoleConnection | null> => {
    const result = await api.get<
      SuccessEnvelope<{ connection: SearchConsoleConnection | null }>
    >(`${BASE}/${projectId}`);
    return result.data.connection;
  },

  getAuthorizeUrl: async (projectId: string): Promise<string> => {
    const result = await api.get<SuccessEnvelope<{ url: string }>>(
      `${BASE}/${projectId}/authorize`,
    );
    return result.data.url;
  },

  // Exchanges the code and returns the caller's properties. Persists nothing:
  // the property still has to be chosen before `connect`.
  completeCallback: async (params: {
    code: string;
    state: string;
  }): Promise<SearchConsoleCallbackResult> => {
    const result = await api.post<SuccessEnvelope<SearchConsoleCallbackResult>>(
      `${BASE}/callback`,
      params,
    );
    return result.data;
  },

  connect: async (params: {
    projectId: string;
    siteUrl: string;
    connectionId: string;
  }): Promise<SearchConsoleConnection> => {
    const { projectId, ...body } = params;
    const result = await api.post<
      SuccessEnvelope<{ connection: SearchConsoleConnection }>
    >(`${BASE}/${projectId}/connect`, body);
    return result.data.connection;
  },

  disconnect: async (projectId: string): Promise<void> => {
    await api.post(`${BASE}/${projectId}/disconnect`);
  },
};

export type SearchTotals = {
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
};

export type SearchQueryRow = SearchTotals & {
  key: string;
  estimatedCompanies: number;
  estimatedRevenue: number;
  /** Share of this query's clicks we could tie back to a known landing page. */
  attributedClickShare: number;
  /** True when the split is not an estimate: one query drove those pages. */
  exact: boolean;
};

export type SearchPageRow = SearchQueryRow & {
  matched: boolean;
  sessions: number;
  identifiedSessions: number;
  companies: number;
  revenue: number;
  orders: number;
};

export type SearchPerformance =
  | { status: 'not_connected' | 'unauthorized' }
  | {
      status: 'ok';
      siteUrl: string;
      firstIncompleteDate: string | null;
      totalsAreSampled: boolean;
      path: string | null;
      totals: SearchTotals;
      queries: SearchQueryRow[];
      pages: SearchPageRow[];
      unmatchedPages: number;
    };

// Mirrors the server's 5-minute result cache. Inside that window a refetch is
// guaranteed the same cached bytes, so it is pure round-trip with no new data.
export const SEARCH_PERFORMANCE_STALE_MS = 5 * 60_000;

export const SearchConsoleReportService = {
  getPerformance: async (params: {
    projectId: string;
    startDate: string;
    endDate: string;
    path?: string;
  }): Promise<SearchPerformance> => {
    const search = new URLSearchParams(params);
    const result = await api.get<SuccessEnvelope<SearchPerformance>>(
      `/search-console/performance?${search.toString()}`,
    );
    return result.data;
  },
};
